// SMS später senden (Runde 6, Agent 6D, S03): Zeitwahl für den SmsKomposer und die Liste der schon geplanten SMS eines Leads.
// Planen speichert nur. Gesendet wird erst, wenn der Versand geplanter SMS freigegeben ist (Einstellungen › SMS), und nur
// zwischen 8 und 21 Uhr. Alle Zeiten in Berliner Ortszeit.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useEchtzeit } from "../api/echtzeit";
import { smsEingangApi, type SmsZeile } from "../api/smsEingang";
import { Icon } from "../components/Icon";
import { useMeldung } from "../components/Meldungen";
import { berlinTeile, berlinZuIso, heute, naechsterMontag, tagPlus, tagText, wochentag } from "../werkzeuge/zeit";
import { ZeitFeld } from "../components/ZeitFeld";

export const SENDEZEIT_VON = 8;
export const SENDEZEIT_BIS = 21;

export interface PlanZeit {
  datum: string; // YYYY-MM-DD, Berlin
  zeit: string; // HH:mm, Berlin
}

/** ISO-Zeitpunkt der Wahl (UTC). */
export function planIso(z: PlanZeit): string {
  return berlinZuIso(z.datum, z.zeit);
}

/** Prüft die Wahl; gibt den Grund zurück, warum sie nicht geht, sonst null. */
export function planFehler(z: PlanZeit, jetzt = Date.now()): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(z.datum) || !/^\d{2}:\d{2}$/.test(z.zeit)) return "Bitte Datum und Uhrzeit wählen.";
  const stunde = Number(z.zeit.slice(0, 2));
  if (stunde < SENDEZEIT_VON || stunde >= SENDEZEIT_BIS) return `SMS gehen nur zwischen ${SENDEZEIT_VON} und ${SENDEZEIT_BIS} Uhr raus.`;
  const ms = new Date(planIso(z)).getTime();
  if (ms < jetzt + 60_000) return "Der Zeitpunkt muss in der Zukunft liegen.";
  if (ms > jetzt + 365 * 86_400_000) return "Geplant werden kann höchstens ein Jahr im Voraus.";
  return null;
}

/** Text der Wahl: „Morgen, 09:00“, „Mo 20.10.2026, 14:00“. */
export function planText(z: PlanZeit): string {
  return `${tagText(z.datum)}, ${z.zeit}`;
}

/** Vorschläge: in einer Stunde (nur im Sendefenster), morgen früh und nachmittags, nächster Montag. */
export function vorschlaege(jetzt = Date.now()): { label: string; wert: PlanZeit }[] {
  const liste: { label: string; wert: PlanZeit }[] = [];
  const ab = new Date(jetzt + 60 * 60_000).toISOString();
  const { datum, zeit } = berlinTeile(ab);
  const rund = `${zeit.slice(0, 2)}:${Number(zeit.slice(3)) < 30 ? "00" : "30"}`;
  if (!planFehler({ datum, zeit: rund }, jetzt)) liste.push({ label: "In 1 Stunde", wert: { datum, zeit: rund } });
  const morgen = tagPlus(heute(), 1);
  liste.push({ label: "Morgen früh", wert: { datum: morgen, zeit: "09:00" } });
  liste.push({ label: "Morgen Nachmittag", wert: { datum: morgen, zeit: "14:00" } });
  const montag = naechsterMontag();
  if (montag !== morgen) liste.push({ label: "Nächster Montag", wert: { datum: montag, zeit: "09:00" } });
  else if (wochentag(morgen) === 1) liste.push({ label: "In einer Woche", wert: { datum: tagPlus(morgen, 6), zeit: "09:00" } });
  return liste;
}

interface WahlProps {
  wert: PlanZeit;
  onChange: (z: PlanZeit) => void;
}

/** Zeile mit Vorschlägen, Datum und Uhrzeit. */
export function SpaeterWahl({ wert, onChange }: WahlProps) {
  const fehler = planFehler(wert);
  const tippen = vorschlaege();
  return (
    <div className="km-spaeter" role="group" aria-label="Wann senden?">
      <div className="km-zeile">
        <span className="km-zeile__label" id="km-spaeter-label">
          Wann
        </span>
        {tippen.map((v) => (
          <button
            key={v.label}
            type="button"
            className={`knopf knopf--klein${v.wert.datum === wert.datum && v.wert.zeit === wert.zeit ? " knopf--an" : ""}`}
            onClick={() => onChange(v.wert)}
            title={planText(v.wert)}
          >
            {v.label}
          </button>
        ))}
        <input
          type="date"
          className="eingabe eingabe--klein km-spaeter__datum"
          aria-label="Datum"
          value={wert.datum}
          min={heute()}
          max={tagPlus(heute(), 365)}
          onChange={(e) => onChange({ ...wert, datum: e.target.value })}
        />
        <ZeitFeld
          className="eingabe eingabe--klein km-spaeter__zeit"
          wert={wert.zeit}
          leerErlaubt={false}
          listeVon={`${String(SENDEZEIT_VON).padStart(2, "0")}:00`}
          listeBis={`${SENDEZEIT_BIS - 1}:45`}
          onChange={(z) => onChange({ ...wert, zeit: z })}
        />
      </div>
      <div className={`km-zeile km-zeile--leise${fehler ? " km-spaeter__fehler" : ""}`} aria-live="polite">
        {fehler ?? `Geht am ${planText(wert)} Uhr raus. SMS gehen nur zwischen ${SENDEZEIT_VON} und ${SENDEZEIT_BIS} Uhr raus.`}
      </div>
    </div>
  );
}

/** Schon geplante SMS dieses Leads mit „Aufheben“; weist darauf hin, solange der Versand nicht freigegeben ist. */
export function GeplanteSms({ leadId, neuLaden }: { leadId: string; neuLaden?: number }) {
  const meldung = useMeldung();
  const [liste, setListe] = useState<SmsZeile[]>([]);
  const [freigabe, setFreigabe] = useState<boolean | undefined>(undefined);
  const [beschaeftigt, setBeschaeftigt] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      const r = await smsEingangApi.geplant();
      setListe(r.data.filter((s) => s.lead_id === leadId));
      setFreigabe(r.freigabe);
    } catch {
      /* die Liste ist eine Zugabe: bei Fehler bleibt sie leer */
    }
  }, [leadId]);

  useEffect(() => {
    void laden();
  }, [laden, neuLaden]);
  useEchtzeit(["sms.updated"], () => void laden());

  if (!liste.length) return null;

  const aufheben = async (s: SmsZeile) => {
    setBeschaeftigt(s.id);
    try {
      await smsEingangApi.planAufheben(s.id);
      setListe((l) => l.filter((x) => x.id !== s.id));
      meldung.ok("Planung aufgehoben.");
    } catch (e) {
      meldung.fehler(e, "Nicht aufgehoben");
    } finally {
      setBeschaeftigt(null);
    }
  };

  return (
    <div className="km-geplant" aria-label="Geplante SMS">
      <div className="km-geplant__kopf">
        <Icon name="uhr" groesse={13} /> Geplant ({liste.length})
        {freigabe === false ? (
          <span className="km-geplant__hinweis">
            Der Versand ist noch nicht eingeschaltet. <Link to="/einstellungen/sms">Einstellungen</Link>
          </span>
        ) : null}
      </div>
      {liste.map((s) => {
        const t = s.geplant_at ? berlinTeile(s.geplant_at) : null;
        return (
          <div key={s.id} className="km-geplant__zeile">
            <span className={`km-geplant__zeit${s.ueberfaellig ? " km-geplant__zeit--ueberfaellig" : ""}`}>{t ? planText(t) : ""}{s.ueberfaellig ? " · überfällig" : ""}</span>
            <span className="km-geplant__text" title={s.text ?? undefined}>
              {s.text}
            </span>
            {s.kann_aufheben ? (
              <button type="button" className="km-link" disabled={beschaeftigt === s.id} onClick={() => void aufheben(s)}>
                Aufheben
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/** Vorbelegung: morgen früh um 9 Uhr. */
export function standardPlanZeit(): PlanZeit {
  return { datum: tagPlus(heute(), 1), zeit: "09:00" };
}
